import '../css/ui/table-with-filters.css';
import '../css/ui/table-with-filters.responsive.css';
import {Enum} from "../enum/enum";
import {UIElement} from "./ui";
import {dateFromStr, formatDate, formatTimestamp} from "../time/TimeCalculator";
import {CalendarDatePicker} from "./CalendarDatePicker";
import {Dropdown, DropdownOption} from "./Dropdown";
import {Base} from "../Base";
import {Loader} from "./Loader";
import {downloadSV, nnTrim, stripTags} from "../util";

class ColumnTypeEnum extends Enum {
    static STRING = new ColumnTypeEnum('string');
    static LINK = new ColumnTypeEnum('link');
    static CODE = new ColumnTypeEnum('code');
    static INTEGER = new ColumnTypeEnum('integer');
    static FLOAT = new ColumnTypeEnum('float');
    static BOOLEAN = new ColumnTypeEnum('boolean');
    static DATE = new ColumnTypeEnum('date');
    static TIMESTAMP = new ColumnTypeEnum('timestamp');

    static getForCode(code) {
        return Enum._getForCode(ColumnTypeEnum, code);
    }

    static exists(code) {
        return Enum._exists(ColumnTypeEnum, code);
    }

    static getPossibleCodes() {
        return Enum._getPossibleCodes(ColumnTypeEnum)
    }

    static getPossibleValues() {
        return Enum._getPossibleValues(ColumnTypeEnum)
    }
}

class SortOrderEnum extends Enum {

    static ASC = new SortOrderEnum('asc', 1);
    static DESC = new SortOrderEnum('desc', -1);

    constructor(code, multiplier) {
        super(code);
        this.multiplier = multiplier;
    }

    getMultiplier() {
        return this.multiplier;
    }

    static getForCode(code) {
        return Enum._getForCode(SortOrderEnum, code);
    }

    static exists(code) {
        return Enum._exists(SortOrderEnum, code);
    }

    static getPossibleCodes() {
        return Enum._getPossibleCodes(SortOrderEnum)
    }

    static getPossibleValues() {
        return Enum._getPossibleValues(SortOrderEnum)
    }
}

class TableColumn extends UIElement {

    getClassName() {
        return "TableColumn";
    }

    /**
     * @param code {string}
     * @param type {ColumnTypeEnum}
     * @param nameKey {string}
     * @param width {number|undefined}
     * @param visible {boolean|undefined}
     * @param sortable {boolean|undefined}
     * @param renderFunc {function|undefined}
     * @param afterRender {function|undefined}
     */
    constructor(code, type, nameKey, width = undefined, visible = true, sortable = true, renderFunc = undefined, afterRender=undefined) {
        super(code);
        this.code = code;
        this.type = type;
        this.nameKey = nameKey;
        this.width = width;
        this.shouldBeRendered = visible;
        this.sortable = sortable;
        this.renderFunc = renderFunc;
        this.clickCallbacks = [];
        this.afterRender = afterRender;
    }

    getUIElement() {
        if(!this.uiElement) {
            this.uiElement = $('<th class="col-' + this.code + '">' + this.getName() + '</th>')
                .css('text-align', this._getAlign())
                .click(() => {
                    if (this.sortable) this.clickCallbacks.forEach(c => c(this))
                });
            if (this.sortable)
                this.uiElement.addClass('sortable');
            this._handleWidth(this.uiElement);
            this.showForWidthManagement();
        }
        return this.uiElement;
    }

    getName() {
        return tumbo.translator.translate(this.nameKey);
    }

    setSort(sortOrder = undefined) {
        const previousOrder = this.sortOrder;
        this.removeSort();
        if(sortOrder) {
            this.sortOrder = sortOrder;
        }else{
            if (!previousOrder)
                this.sortOrder = SortOrderEnum.ASC;
            if (previousOrder === SortOrderEnum.ASC)
                this.sortOrder = SortOrderEnum.DESC;
            if (previousOrder === SortOrderEnum.DESC)
                return;
        }
        this.uiElement.addClass('sorted ' + this.sortOrder.getCode());
    }

    getSort() {
        return this.sortOrder;
    }

    isSorted() {
        return !!this.sortOrder;
    }

    removeSort() {
        this.uiElement.removeClass('sorted').removeClass(SortOrderEnum.ASC.getCode()).removeClass(SortOrderEnum.DESC.getCode());
        this.sortOrder = undefined;
    }

    onClick(callback) {
        this.clickCallbacks.push(callback);
    }

    _getAlign() {
        if (this.type === ColumnTypeEnum.INTEGER || this.type === ColumnTypeEnum.FLOAT || this.type === ColumnTypeEnum.DATE || this.type === ColumnTypeEnum.TIMESTAMP)
            return 'right';
        if (this.type === ColumnTypeEnum.BOOLEAN)
            return 'center';
        return 'left';
    }

    getCode() {
        return this.code;
    }

    getWidth() {
        return this.width;
    }

    _handleWidth(elem) {
        elem
            .css('width', this.width ? this.width + 'px' : '')
            .css('max-width', this.width ? this.width + 'px' : '');
    }

    getCell(value, row, elem) {
        const td = $('<td class="col-' + this.code + '"></td>')
            .data('col', this.code)
            .css('text-align', this._getAlign());
        if(!this.isEffectivelyVisible())
            td.hide();
        else td.show();
        if(this.isMultiline())
            td.addClass('multiline');
        else td.removeClass('multiline');
        if (!this.renderFunc && (value === undefined || value === null))
            td.html('--');
        else {
            if (this.renderFunc) {
                td.html($('<div></div>').append(this.renderFunc(value, row, elem)));
            } else {
                td.html($('<div></div>').append(this.renderValue(value)));
            }
        }

        if(this.afterRender){
            this.afterRender(value, elem, row, td);
        }

        return td;
    }

    compare(value1, value2) {
        if (value1 === null || value1 === undefined) {
            return this.sortOrder.getMultiplier();
        }
        if (value2 === null || value2 === undefined) {
            return this.sortOrder.getMultiplier() * -1;
        }
        switch (this.type) {
            case ColumnTypeEnum.STRING:
            case ColumnTypeEnum.LINK:
            case ColumnTypeEnum.CODE:
                return String(value1).localeCompare(String(value2)) * this.sortOrder.getMultiplier();
            case ColumnTypeEnum.BOOLEAN:
                return (value1 ? 1 : value2) * this.sortOrder.getMultiplier();
            case ColumnTypeEnum.FLOAT:
            case ColumnTypeEnum.INTEGER:
                return (value1 - value2) * this.sortOrder.getMultiplier();
            case ColumnTypeEnum.DATE:
            case ColumnTypeEnum.TIMESTAMP:
                return (dateFromStr(value1).getTime() - dateFromStr(value2).getTime()) * this.sortOrder.getMultiplier();
        }
        return 0;
    }

    renderValue(value) {
        switch (this.type) {
            case ColumnTypeEnum.STRING:
                return value;
            case ColumnTypeEnum.CODE:
                return '<pre>' + value + '</pre>';
            case ColumnTypeEnum.LINK:
                return '<a href="'+value+'" target="_blank">' + value.cropEnd(20) + '</a>';
            case ColumnTypeEnum.BOOLEAN:
                return tumbo.translator.translate('boolean.' + value);
            case ColumnTypeEnum.FLOAT:
                return value;
            case ColumnTypeEnum.INTEGER:
                return value;
            case ColumnTypeEnum.DATE:
                return this._formatDate(value);
            case ColumnTypeEnum.TIMESTAMP:
                const date = toDate(value);
                if (!date)
                    return '--';
                const datePart = this._formatDate(date);
                return datePart + ' <span class="time">' + [date.getHours().pad(2), date.getMinutes().pad(2), date.getSeconds().pad(2)].join(':');
        }
    }

    _formatDate(value) {
        const date = dateFromStr(value);
        if (!date)
            return '--';
        return [date.getFullYear(), (date.getMonth() + 1).pad(2), date.getDate().pad(2)].join('-');
    }

    hideForWidthManagement() {
        this.hiddenForWidthManagement = true;
        this.handleVisibility();
    }

    showForWidthManagement() {
        this.hiddenForWidthManagement = false;
        this.handleVisibility();
    }

    hide() {
        this.hidden = true;
        this.handleVisibility();
        return this;
    }

    show() {
        this.hidden = false;
        this.handleVisibility();
        return this;
    }

    handleVisibility(){
        if(this.isEffectivelyVisible())
            this.getUIElement().removeClass('hidden');
        else
            this.getUIElement().addClass('hidden');
    }

    isEffectivelyVisible() {
        return !this.hiddenForWidthManagement && !this.hidden;
    }

    isVisibleAfterWidthManagement() {
        return !this.hiddenForWidthManagement;
    }


    isVisible() {
        return !this.hidden;
    }

    shouldRender() {
        return this.shouldBeRendered;
    }

    setMultiline(multiline){
        this.multiline = !!multiline;
        const uiElement = this.getUIElement();
        const tds = $('tbody td.col-'+this.code,$(uiElement.parents('table')[0]));
        if(this.multiline) {
            tds.addClass('multiline');
            uiElement.addClass('multiline');
        }else{
            tds.removeClass('multiline');
            uiElement.removeClass('multiline');
        }
        return this;
    }

    isMultiline(){
        return !!this.multiline;
    }
}

class AbstractTableFilter extends UIElement {

    getClassName() {
        return "AbstractTableFilter";
    }

    /**
     * @param code {string}
     * @param nameKey {string}
     * @param filterFunction {function} A function that receives the table columns, a table row and the filter value and returns true if the row should be shown according to the filter
     */
    constructor(code, nameKey, filterFunction) {
        super(code);
        this.code = code;
        this.nameKey = nameKey;
        this.filterFunction = filterFunction;
        this.value = this.getSessionProp(this.getValueSessionPropCode());
        this.onChangeCallbacks = new Set();
        this.onEnterCallbacks = new Set();
    }

    getCode() {
        return this.code;
    }

    _executeOnChange() {
        this.setSessionProp(this.getValueSessionPropCode(), this.value);
        this.onChangeCallbacks.forEach(c => c(this.value));
    }

    onChange(callback) {
        if(callback)
            this.onChangeCallbacks.add(callback);
        return this;
    }

    onEnterKey(callback) {
        if(callback)
            this.onEnterCallbacks.add(callback);
        return this;
    }

    getValueSessionPropCode() {
        return this.id + '_tablefilter';
    }

    getUIElement() {
        if (!this.uiElement) {
            const filterElement = this.getFilterElement();
            filterElement.on('keyup', (e) => {
                const code = e.which;
                if (code === 13) {
                    this.onEnterCallbacks.forEach(c => c());
                    return false;
                }
            })
            this.uiElement = $('<div></div>').addClass('filter-container').append(
                $('<label>' + tumbo.translator.translate(this.nameKey) + '</label>').append(filterElement)
            );
        }
        return this.uiElement;
    }

    getFilterElement() {
    }

    getValue() {
        return this.value;
    }

    filter(columns, row) {
        return !this.value || this.value.length === 0 || this.filterFunction(columns, row, this.value.trim());
    }
}

class TextTableFilter extends AbstractTableFilter {
    getClassName() {
        return "TextTableFilter";
    }

    getFilterElement() {
        const input = $('<input autocomplete="off" type="text" id="' + this.code + '_filter"/>')
            .attr('placeholder', tumbo.translator.translate('table.filter.placeholder.text'))
            .val(this.value);
        input.change(() => {
            this.value = $('input', this.uiElement).val();
            this.logStat("CHANGE_VALUE");
            this._executeOnChange();
        })
        return input;
    }
}

class DateTableFilter extends AbstractTableFilter {
    getClassName() {
        return "DateTableFilter";
    }

    constructor(code, nameKey, filterFunction) {
        super(code, nameKey, filterFunction);
        this.shouldApplyOnChange = true;
        const storedVal = this.getSessionProp(this.getValueSessionPropCode());
        this.value = isNaN(dateFromStr(storedVal))?undefined:formatDate(dateFromStr(storedVal));
    }

    getFilterElement() {
        return this.getDatePicker().onChange((val) => {
            this.value = !isNaN(val)?formatDate(val):undefined;
            this.logStat("CHANGE_VALUE");
            this._executeOnChange();
            if (this.shouldApplyOnChange) {
                this.onEnterCallbacks.forEach(c => c());
            }
        }).getUIElement();
    }

    getDatePicker() {
        return new CalendarDatePicker(this.code + '_datefilter', tumbo.translator.translate('table.filter.placeholder.date'), this.value, true);
    }

    setApplyOnChange(shouldApply) {
        this.shouldApplyOnChange = shouldApply;
        return this;
    }
}

class TimestampTableFilter extends DateTableFilter {
    getClassName() {
        return "TimestampTableFilter";
    }

    constructor(code, nameKey, filterFunction) {
        super(code, nameKey, filterFunction);
        const storedVal = this.getSessionProp(this.getValueSessionPropCode());
        this.value = isNaN(dateFromStr(storedVal))?undefined:formatTimestamp(dateFromStr(storedVal));
    }

    getFilterElement() {
        return this.getDatePicker().onChange((val) => {
            this.value = formatTimestamp(val);
            this.logStat("CHANGE_VALUE");
            this._executeOnChange();
            if (this.shouldApplyOnChange) {
                this.onEnterCallbacks.forEach(c => c());
            }
        }).getUIElement();
    }

    getDatePicker() {
        return new CalendarDatePicker(this.code + '_datefilter', tumbo.translator.translate('table.filter.placeholder.timestamp'), this.value, false);
    }
}

class DropdownTableFilter extends AbstractTableFilter {

    getClassName() {
        return "DropdownTableFilter";
    }

    /**
     *
     * @param code {string}
     * @param nameKey {string}
     * @param mandatory {boolean}
     * @param possibleValues {Array.<string>}
     * @param possibleValueNameKeys {Array.<string>}
     * @param parentValues {Array.<string>}
     * @param parentFilter {DropdownTableFilter}
     */
    constructor(code, nameKey, mandatory, possibleValues, possibleValueNameKeys, parentValues, parentFilter) {
        super(code, nameKey, (columns, row, value) => {
            const index = columns.map(c => c.getCode()).indexOf(this.code);
            const rowVal = row[index] ? row[index].toString().trim().toLowerCase() : undefined;
            return this.dropDown.getValue() === rowVal;
        });
        this.mandatory = mandatory;
        this.possibleValues = possibleValues;
        this.possibleValueNameKeys = possibleValueNameKeys;
        this.parentValues = parentValues;
        this.parentFilter = parentFilter;
    }

    setApplyOnChange(shouldApply) {
        this.shouldApplyOnChange = shouldApply;
        return this;
    }

    getFilterElement() {
        if(!this.filterElement) {
            const options = [];
            for (let i = 0; i < this.possibleValues.length; i++) {
                options.push(new DropdownOption(this.possibleValueNameKeys[i], this.possibleValues[i], this.parentValues?this.parentValues[i]:undefined));
            }
            this.dropDown = new Dropdown(this.id + '_dropdown', options, this.value, this.mandatory, 'table.filter.dropDown.choose', (dropdown) => {
                this.value = dropdown.getValue();
                this._executeOnChange();
                if (this.shouldApplyOnChange) {
                    this.onEnterCallbacks.forEach(c => c());
                }
            },this.parentFilter?this.parentFilter.getDropdown():undefined);
            this.filterElement = this.dropDown.getUIElement();
        }
        return this.filterElement;
    }

    getDropdown(){
        this.getFilterElement();
        return this.dropDown;
    }

    setValue(value){
        this.getDropdown().setValue(value);
        return this;
    }
}

class AbstractTableAction extends UIElement {

    getClassName() {
        return "AbstractTableAction";
    }

    getUIElement() {
        if (!this.uiElement) {
            this.uiElement = $('<div class="action-container"></div>').append(
                this.getButtonElement().attr('title', this.getName()).click(() => this.onClick())
            );
        }
        return this.uiElement;
    }

    /**
     * Override to define behavior
     * @return {AbstractTableAction}
     */
    onClick() {
    }

    /**
     * Override to define action button
     * @return {jQuery}
     */
    getButtonElement() {
    }

    /**
     * @return {string}
     */
    getName() {
    }

    setTable(table) {
        this.table = table;
        return this;
    }
}

class AbstractRowAction extends Base {

    getClassName() {
        return "AbstractRowAction";
    }

    getCellContent(elem) {
        const cellContent = $('<div class="row-action-container"></div>');
        const button = this.getButtonElement().attr('title', this.getName());
        if(!this.isEnabled(elem)){
            button.addClass('disabled').attr('disabled','disabled');
        }
        button.click(() => {
            if (this.onClick(elem)) {
                $(cellContent.parents('tr')[0]).addClass('selected');
            }
        });
        return cellContent.append(button);
    }

    getUIElement() {
        this.getButtonElement();
    }

    getButtonElement() {
    }

    /**
     * Override to define row action behaviour
     * @param element {Object} The row data as a dictionary of column code to value
     * @return {boolean} if true the row should appear as selected
     */
    onClick(element) {
    }

    /**
     * Override to define row action appearence (appears enabled for all rows if not overriden)
     * @param element {Object} The row data as a dictionary of column code to value
     * @return {boolean} if true action will be enabled
     */
    isEnabled(element) {
        return true;
    }

    /**
     * Override to define row action appearence (appears for all rows if not overriden)
     * @param element {Object} The row data as a dictionary of column code to value
     * @return {boolean} if true action will be shown
     */
    hasAction(element) {
        return true;
    }

    /**
     * @return {string}
     */
    getName() {
    }

    setTable(table) {
        this.table = table;
        return this;
    }
}

class SimpleExportTSVTableAction extends AbstractTableAction{

    getClassName() {
        return 'SimpleExportTSVTableAction';
    }

    onClick() {
        const cols = this.table.getColumns();
        const rows = [cols.map(c=>c.getName().replaceAll('\n',' ').replaceAll('\r',' '))];
        this.table.getData().forEach(tableRow=>{
            rows.push(cols.map((col,index)=>stripTags(col?.renderValue(tableRow[index]))?.replaceAll('\n',' ').replaceAll('\r',' ')));
        });
        downloadSV('\t', rows,'export_'+formatTimestamp(new Date())+'.tsv')
    }

    getButtonElement() {
        return $('<button>').addClass('button action green export-tsv').attr('type','button');
    }
}

class TableWithFilters extends UIElement {

    getClassName() {
        return "TableWithFilters";
    }

    /**
     *
     * @param id {string}
     * @param columns {Array.<TableColumn>}
     * @param filters {Array.<AbstractTableFilter>}
     * @param actions {Array.<AbstractTableAction>}
     * @param rowActions {Array.<AbstractRowAction>}
     * @param data {Array.<Array.<*>>|Array.<Object>}
     * @param pageSize {int|undefined}
     * @param initialSort {object|undefined}
     */
    constructor(id, columns, filters, actions = [], rowActions = [], data, pageSize = 50, initialSort=undefined) {
        super(id);
        this.columns = columns;
        this.filters = filters;
        this.actions = actions.map(a => a.setTable(this));
        this.rowActions = rowActions.map(a => a.setTable(this));
        this.data = data && data.length>0 && !Array.isArray(data[0]) ? data.map(obj => columns.map(c => obj[c.getCode()])) : data;
        this.pageSize = pageSize;
        this.page = this.getSessionProp("page", 0);

        this.rowProcessors = [];
        this.initialSort = initialSort;

        this.sortClick = (column) => {
            this.initialSort = undefined;
            column.setSort();
            this.logStat("SORT_COLUMN {"+column.getLogId()+"}: " + column.getSort());
            this.refresh();
        }
        this.getShowableColumns().forEach(c => c.onClick(this.sortClick));

        this.onAdd2DOM(()=>this._handleVisibleColumns());
    }

    getData(){
        return this.data;
    }

    getRequestData() {
        return new ServerPagedTableRequestData(this.filters, this.columns, this.pageSize, this.page);
    }

    addRowProcessor(processor) {
        this.rowProcessors.push(processor);
        return this;
    }

    unselect() {
        $('table tbody tr', this.uiElement).removeClass('selected');
    }

    getUIElement() {
        if (!this.uiElement) {
            this.uiElement = $('<div class="table-with-filters-container">' +
                '   <div class="upload-result"></div>' +
                '   <div class="filters-container"></div>' +
                '   <div class="pager-container first"></div>' +
                '   <table>' +
                '       <thead>' +
                '           <tr></tr>' +
                '       </thead>' +
                '       <tbody></tbody>' +
                '   </table>' +
                '   <div class="pager-container second"></div>' +
                '</div>'
            );
            this.loader = new Loader(this.id + '_loader', this.uiElement);
            this.getShowableColumns()
                .forEach(c => $('table thead tr', this.uiElement).append(c.getUIElement()));

            if (this.rowActions.length > 0 || this.actions.length > 0) {
                const th = $('<th/>').addClass('actions').css('width', Math.max(this._getRowActionsWidth(), this._getActionsWidth()) + 'px');
                $('table thead tr', this.uiElement).append(th);
                this.actions.forEach(a => th.append(a.getUIElement()));
            }

            this._renderFilters();
            this.refresh();
        }
        return this.uiElement;
    }

    hidePageAndCount(){
        this.shouldHidePageAndCount = true;
        $('.pager-container',this.uiElement).hide();
        return this;
    }

    _getRowActionsWidth() {
        return this.rowActions.length > 0 ? this.rowActions.length * 30 + 5 : 0;
    }

    _getActionsWidth() {
        return this.actions.length > 0 ? this.actions.length * 30 + 5 : 0;
    }

    _renderFilters() {
        const filtersContainer = $('.filters-container', this.uiElement);
        if (this.filters && this.filters.length > 0) {
            const button = $('<button type="button" class="action">' + tumbo.translator.translate('table.filter.apply') + '</button>');
            this.filters.forEach(f => filtersContainer.append(f.onEnterKey(() => button.click()).getUIElement()));
            button.click(() => {
                this.logStat("APPLY_FILTERS");
                this.refresh();
            });
            filtersContainer.append(button)
        } else {
            filtersContainer.hide();
        }
    }

    showLoader() {
        this.loader.show();
    }

    hideLoader() {
        this.loader.hide();
    }

    refresh() {
        this.showLoader();
        this._filterData();
        this._sortData();
        this._fillTable();
        this._handlePages();
        this.hideLoader();
        setTimeout(()=>this._handleVisibleColumns(),100)
    }

    _filterData() {
        this.filteredData = this.filters && this.filters.length > 0
            ? this.data.filter(row => {
                for (let f in this.filters) {
                    if (!this.filters[f].filter(this.columns, row))
                        return false;
                }
                return true;
            })
            : this.data;
        this.rowsCount = this.filteredData.length;
        this.pagesNr = Math.ceil(this.rowsCount / this.pageSize);
        this.page = 0;
    }

    _sortData() {
        const showableColumns = this.getShowableColumns();
        let sortedCol;
        if(this.initialSort) {
            const colCode = Object.keys(this.initialSort)[0];
            sortedCol = showableColumns.filter(c=>c.getCode()===colCode)[0];
            if(sortedCol) {
                sortedCol.setSort(this.initialSort[colCode]);
            }
        } else {
            sortedCol = showableColumns.filter(c => c.isSorted())[0];
        }

        if (sortedCol) {
            const index = this.columns.map(c => c.getCode()).indexOf(sortedCol.getCode());
            this.filteredData.sort((row1, row2) => {
                return sortedCol.compare(row1[index], row2[index]);
            });
        }
    }

    _fillTable() {
        const data2Add = this._getPagedData();
        const tbody = $('table tbody', this.uiElement).empty();
        data2Add.map(row => {
            const tr = $('<tr></tr>');

            const elem = {};
            for (let i = 0; i < this.columns.length; i++) {
                elem[this.columns[i].getCode()] = row[i];
            }
            Object.keys(row)
                .filter(k=>k!=='length')
                .forEach(k=>elem[k]=row[k]);

            let i = 0;
            this.columns
                .forEach(c => {
                    if (c.shouldRender()) {
                        tr.append(c.getCell(row[i], row, elem))
                    }
                    i++;
                });
            if (this.rowActions.length > 0) {
                const actionsTd = $('<td>').addClass('row-actions');
                this.rowActions
                    .filter(a=>a.hasAction(elem))
                    .map(a => a.getCellContent(elem))
                    .forEach(div => actionsTd.append(div));
                tr.append(actionsTd);
            }
            this.rowProcessors.forEach(p => p(row, tr));
            return tr;
        }).forEach(tr => tbody.append(tr));
        if (data2Add.length === 0) {
            tbody.append($('<tr></tr>').append(
                $('<td></td>')
                    .addClass('empty')
                    .attr('colspan', this.getShowableColumns().filter(c => c.isEffectivelyVisible()).length)
                    .html(tumbo.translator.translate('table.empty'))
            ));
        }
    }

    _getPagedData() {
        return this.filteredData.slice(
            Math.min(this.rowsCount - 1, this.page * this.pageSize),
            Math.min(this.rowsCount, (this.page + 1) * this.pageSize)
        );
    }

    _handlePages() {
        if(!this.rowsCount || !this.pagesNr){
            const ul = $('<ul class="pager"></ul>');
            const first = $('<li>&lt;&lt;</li>')
                .click(() => {
                    this.setPage(0);
                    this.logStat("FIRST_PAGE");
                })
                .addClass(this.page === 0 ? 'disabled' : '');
            const previous = $('<li>&lt;</li>')
                .click(() => {
                    if(!previous.hasClass('disabled')) {
                        this.setPage(this.page - 1)
                        this.logStat("PREVIOUS_PAGE");
                    }
                })
                .addClass(this.page === 0 ? 'disabled' : '');
            const current = $('<li></li>').html(this.page + 1).addClass('page');
            const hasNext = this.filteredData.length===this.pageSize;
            const next = $('<li>&gt;</li>')
                .click(() => {
                    if(!next.hasClass('disabled')) {
                        this.setPage(this.page + 1);
                        this.logStat("NEXT_PAGE");
                    }
                })
                .addClass(!hasNext ? 'disabled' : '');
            ul.append(first).append(previous).append(current).append(current).append(next);
            $('.pager-container', this.uiElement).empty().show().append(ul);
            if(!hasNext || this.shouldHidePageAndCount)
                $('.pager-container.first', this.uiElement).hide();

        } else {
            if (this.pagesNr > 1) {
                const ul = $('<ul class="pager"></ul>');
                const first = $('<li>&lt;&lt;</li>')
                    .click(() => {
                        if(!first.hasClass('disabled')) {
                            this.logStat("FIRST_PAGE");
                            this.setPage(0);
                        }
                    }).addClass(this.page === 0 ? 'disabled' : '');
                const previous = $('<li>&lt;</li>')
                    .click(() => {
                        if(!previous.hasClass('disabled')) {
                            this.logStat("PREVIOUS_PAGE");
                            this.setPage(this.page - 1);
                        }
                    }).addClass(this.page === 0 ? 'disabled' : '');
                const current = $('<li></li>').html(this.page + 1).addClass('page');
                const next = $('<li>&gt;</li>')
                    .click(() => {
                        if(!next.hasClass('disabled')) {
                            this.logStat("NEXT_PAGE");
                            this.setPage(this.page + 1);
                        }
                    }).addClass(this.page === this.pagesNr - 1 ? 'disabled' : '');
                const last = $('<li>&gt;&gt;</li>')
                    .click(() => {
                        if(!last.hasClass('disabled')) {
                            this.setPage(this.pagesNr - 1);
                            this.logStat("LAST_PAGE");
                        }
                    }).addClass(this.page === this.pagesNr - 1 ? 'disabled' : '');
                ul.append(first).append(previous).append(current).append(current).append(next).append(last);
                $('.pager-container', this.uiElement)
                    .empty().show()
                    .append($('<span class="context summary">' + tumbo.translator.translate('table.pager.summary', [
                        ((this.page) * this.pageSize + 1).toString(),
                        Math.min(this.rowsCount, ((this.page + 1) * this.pageSize)).toString(),
                        this.rowsCount.toString()
                    ]) + '</span>'))
                    .append(ul);
            } else {
                $('.pager-container', this.uiElement).empty().show().append($('<span class="context summary">' + tumbo.translator.translate('table.summary', [
                    this.rowsCount.toString()
                ]) + '</span>'));
            }
            if (this.rowsCount < 10 || this.shouldHidePageAndCount) {
                $('.pager-container.first', this.uiElement).hide();
            }
        }
    }

    setPage(page) {
        const fixedPage = Math.min(this.pagesNr!==undefined?(this.pagesNr - 1):Number.MAX_VALUE, Math.max(0, page));
        if (fixedPage !== this.page) {
            this.page = fixedPage;
            this.setSessionProp("page", this.page);
            $('.pager-container .pager li', this.uiElement).each(function () {
                if ($(this).data('page') !== this.page)
                    $(this).removeClass('disabled');
                else $(this).addClass('disabled');
            });
            this._onSetPage();
        }
    }

    _onSetPage() {
        this._fillTable();
        this._handlePages();
    }

    setContainerSizeAndPosition(isSmallContainerWidth, isSmallContainerHeight, width, height, containerAbsolutePosition) {
        this._handleVisibleColumns();
    }

    _handleVisibleColumns () {
        const MIN_WIDTH = this.isSmallContainerWidth ? 65 : 80;
        const visibleColumns = () => this.getShowableColumns()
            .filter(c => c.isEffectivelyVisible());
        const minTableWidth = () => visibleColumns()
            .map(c => c.getWidth() !== null && c.getWidth() !== undefined ? c.getWidth() : MIN_WIDTH)
            .reduce((a, b) => a + b, 0) + this._getRowActionsWidth() + this._getActionsWidth();
        const lastVisibleColumn = () => {
            const showableColumns = this.getShowableColumns();
            for (let i = showableColumns.length - 1; i >= 0; i--) {
                if (showableColumns[i].isEffectivelyVisible())
                    return showableColumns[i];
            }
        }
        const firstHiddenColumn = () => {
            const showableColumns = this.getShowableColumns();
            for (let i = 0; i < showableColumns.length; i++)
                if (!showableColumns[i].isEffectivelyVisible())
                    return showableColumns[i];
        }
        const parentWidth = () => this.uiElement.parent().width();

        let justHidden = false;
        while (visibleColumns().length >= 1 && parentWidth() < minTableWidth()) {
            const visibleColumn = lastVisibleColumn();
            visibleColumn.hideForWidthManagement();
            $('table tbody tr td.col-' + visibleColumn.getCode(), this.uiElement).addClass('hidden');
            justHidden = true;
        }

        while (!justHidden && visibleColumns().length < this.getShowableColumns().length && parentWidth() - minTableWidth() > MIN_WIDTH) {
            const hiddenColumn = firstHiddenColumn();
            hiddenColumn.showForWidthManagement();
            $('table tbody tr td.col-' + hiddenColumn.getCode(), this.uiElement).removeClass('hidden');
        }
    }

    getColumns(){
        return this.columns;
    }

    getShowableColumns() {
        return this.columns.filter(c => c.shouldRender() && c.isVisible());
    }
}

class ServerPagedTableRequestData extends Base {
    /**
     *
     * @param filters {Array.<AbstractTableFilter>}
     * @param columns {Array.<TableColumn>}
     * @param pageSize {number} integer
     * @param page {number} integer
     * @param justCount {boolean}
     */
    constructor(filters, columns, pageSize, page, justCount) {
        super();
        this.filters = {};
        filters
            .filter(f => nnTrim(f.getValue()) !== undefined)
            .forEach(f => this.filters[f.getCode()] = f.getValue());
        this.sort = columns
            .filter(c => c.isSorted())
            .map(c => c.getCode() + ';' + c.getSort().getCode()).join(',');
        this.pageSize = pageSize;
        this.page = page;
        this.justCount = !!(justCount);
    }

    getRequestParams() {
        const params = {
            page: this.page+1,
            pageSize: this.pageSize
        };
        if(this.sort)
            params['sorting']=this.sort;
        if(this.justCount)
            params['count']=true;
        Object.keys(this.filters)
            .filter(p=>!!nnTrim(this.filters[p]))
            .forEach(p => params[p] = nnTrim(this.filters[p]));
        return params;
    }
}

class ServerPagedTableWithFilters extends TableWithFilters {

    getClassName() {
        return "ServerPagedTableWithFilters";
    }

    /**
     *
     * @param id {string}
     * @param columns {Array.<TableColumn>}
     * @param filters {Array.<AbstractTableFilter>}
     * @param actions {Array.<AbstractTableAction>}
     * @param rowActions {Array.<AbstractRowAction>}
     * @param dataRetriever {function} takes ServerPagedTableRequestData and a callback for when data is received
     * @param maxRowsPerPage {int|undefined}
     * @param sendCountRequests {boolean} If true sends a prior request just to count the amount for rows that match the current filters
     */
    constructor(id, columns, filters, actions, rowActions, dataRetriever, maxRowsPerPage = 50, sendCountRequests=false) {
        super(id, columns, filters, actions, rowActions, undefined, maxRowsPerPage);
        this.dataRetriever = dataRetriever;
        this.sendCountRequests = sendCountRequests;
    }

    refresh() {
        this.showLoader();

        const getData = () => {
            const reqData = new ServerPagedTableRequestData(this.filters, this.columns, this.pageSize, this.page, false);
            this.dataRetriever(reqData, (data) => {
                this.data = data;
                this.filteredData = this.data;
                this._fillTable();
                this._handlePages();
                this.hideLoader();
            });
        }

        const reqDataCount = new ServerPagedTableRequestData(this.filters, this.columns, this.pageSize, this.page, true);
        if(this.sendCountRequests) {
            this.dataRetriever(reqDataCount, (countData) => {
                this.rowsCount = countData[1][0];
                this.pagesNr = Math.ceil(this.rowsCount / this.pageSize);
                this.page = Math.min(this.pagesNr, this.page);
                getData();
            });
        } else {
            this.rowsCount = undefined;
            this.pagesNr = undefined;
            getData();
        }
    }

    _onSetPage() {
        this.refresh();
    }

    _getPagedData() {
        return this.data;
    }
}

export {ColumnTypeEnum, SortOrderEnum,TableColumn, AbstractTableFilter, TextTableFilter, DateTableFilter, TimestampTableFilter, DropdownTableFilter, AbstractTableAction, SimpleExportTSVTableAction, AbstractRowAction, TableWithFilters, ServerPagedTableRequestData, ServerPagedTableWithFilters }